import { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  X, Clapperboard, Wand2, Film, Image as ImageIcon, Loader2, Download, Upload, AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusPill = ({ status }) => {
  const map = {
    queued: { t: "Sıraya alındı", c: "text-[#f59e0b]" },
    processing: { t: "İşleniyor... (1-3 dk sürebilir)", c: "text-[#00a884]" },
    done: { t: "Hazır", c: "text-[#00a884]" },
    error: { t: "Hata", c: "text-[#ef4444]" },
  };
  const s = map[status] || map.queued;
  return <span className={`text-xs font-semibold ${s.c}`} data-testid="video-status-pill">{s.t}</span>;
};

const ResultVideo = ({ task, testid }) => {
  if (!task || task.status !== "done" || !task.url) return null;
  const full = `${BACKEND_URL}${task.url}`;
  return (
    <div className="mt-3 space-y-2" data-testid={`${testid}-result`}>
      <video src={full} controls playsInline className="w-full rounded-xl bg-black max-h-[320px]" data-testid={`${testid}-player`} />
      <a href={full} target="_blank" rel="noreferrer" download data-testid={`${testid}-download`}
        className="inline-flex items-center gap-2 text-sm font-medium text-[#00a884] hover:text-[#06cf9c]">
        <Download size={16} /> Videoyu indir
      </a>
    </div>
  );
};

const Segmented = ({ options, value, onChange, testid }) => (
  <div className="flex gap-1.5 flex-wrap" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.v}
        onClick={() => onChange(o.v)}
        data-testid={`${testid}-${o.v}`}
        className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${value === o.v ? "bg-[#00a884] text-[#0b141a] border-[#00a884]" : "bg-[#202c33] text-[#e9edef] border-[#2a3942] hover:border-[#00a884]/50"}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const VideoStudio = ({ onClose, sessionId }) => {
  const [tab, setTab] = useState("gen"); // gen | i2v | gray

  // --- Metinden video ---
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("16:9");
  const [quality, setQuality] = useState("720p");
  const [duration, setDuration] = useState("kisa");
  const [genTask, setGenTask] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [genErr, setGenErr] = useState("");

  // --- Görselden video ---
  const [i2vFile, setI2vFile] = useState(null);
  const [i2vPrompt, setI2vPrompt] = useState("");
  const [i2vTask, setI2vTask] = useState(null);
  const [i2vBusy, setI2vBusy] = useState(false);
  const [i2vErr, setI2vErr] = useState("");
  const i2vRef = useRef(null);

  // --- Gri tonlama ---
  const [file, setFile] = useState(null);
  const [grayTask, setGrayTask] = useState(null);
  const [grayBusy, setGrayBusy] = useState(false);
  const [grayErr, setGrayErr] = useState("");
  const fileRef = useRef(null);

  const pollRef = useRef({});
  useEffect(() => () => Object.values(pollRef.current).forEach((id) => clearInterval(id)), []);

  const pollTask = (taskId, setTask, setBusy, setErr) => {
    if (pollRef.current[taskId]) clearInterval(pollRef.current[taskId]);
    pollRef.current[taskId] = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/video/status/${taskId}`);
        setTask(data);
        if (data.status === "done" || data.status === "error") {
          clearInterval(pollRef.current[taskId]);
          delete pollRef.current[taskId];
          setBusy(false);
          if (data.status === "error") setErr(data.error || "İşlem başarısız oldu.");
        }
      } catch (e) {
        clearInterval(pollRef.current[taskId]);
        delete pollRef.current[taskId];
        setBusy(false);
        setErr("Durum alınamadı.");
      }
    }, 3000);
  };

  const startGenerate = async () => {
    if (prompt.trim().length < 3 || genBusy) return;
    setGenBusy(true); setGenErr(""); setGenTask({ status: "queued" });
    try {
      const { data } = await axios.post(`${API}/video/generate`, {
        prompt, aspect_ratio: aspect, quality, duration, session_id: sessionId,
      });
      setGenTask({ status: "queued", task_id: data.task_id });
      pollTask(data.task_id, setGenTask, setGenBusy, setGenErr);
    } catch (e) {
      setGenBusy(false); setGenTask(null);
      setGenErr(e.response?.data?.detail || "Video başlatılamadı.");
    }
  };

  const startI2V = async () => {
    if (!i2vFile || i2vPrompt.trim().length < 3 || i2vBusy) return;
    setI2vBusy(true); setI2vErr(""); setI2vTask({ status: "queued" });
    try {
      const fd = new FormData();
      fd.append("file", i2vFile);
      fd.append("prompt", i2vPrompt);
      if (sessionId) fd.append("session_id", sessionId);
      const { data } = await axios.post(`${API}/video/image-to-video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setI2vTask({ status: "queued", task_id: data.task_id });
      pollTask(data.task_id, setI2vTask, setI2vBusy, setI2vErr);
    } catch (e) {
      setI2vBusy(false); setI2vTask(null);
      setI2vErr(e.response?.data?.detail || "Görselden video başlatılamadı.");
    }
  };

  const startGrayscale = async () => {
    if (!file || grayBusy) return;
    setGrayBusy(true); setGrayErr(""); setGrayTask({ status: "queued" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (sessionId) fd.append("session_id", sessionId);
      const { data } = await axios.post(`${API}/video/grayscale`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGrayTask({ status: "queued", task_id: data.task_id });
      pollTask(data.task_id, setGrayTask, setGrayBusy, setGrayErr);
    } catch (e) {
      setGrayBusy(false); setGrayTask(null);
      setGrayErr(e.response?.data?.detail || "Gri tonlama başlatılamadı.");
    }
  };

  const tabs = [
    { k: "gen", label: "Metinden Video", icon: Wand2 },
    { k: "i2v", label: "Görselden Video", icon: ImageIcon },
    { k: "gray", label: "Gri Tonlama", icon: Film },
  ];

  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" data-testid="video-studio" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#111b21] border border-[#2a3942] rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a3942] bg-[#202c33]">
          <div className="flex items-center gap-2">
            <Clapperboard size={20} className="text-[#00a884]" />
            <h2 className="text-base font-semibold text-[#e9edef]">Video Stüdyo</h2>
          </div>
          <button onClick={onClose} data-testid="video-studio-close" className="p-1.5 rounded-full text-[#8696a0] hover:text-[#e9edef] hover:bg-[#0b141a]/50">
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b border-[#2a3942] bg-[#111b21]">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`video-tab-${t.k}`}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs sm:text-sm font-medium transition-colors ${active ? "text-[#00a884] border-b-2 border-[#00a884] bg-[#202c33]/40" : "text-[#8696a0] hover:text-[#e9edef]"}`}>
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4 overflow-y-auto">
          {tab === "gen" && (
            <div className="space-y-3" data-testid="video-gen-tab">
              <p className="text-[#8696a0] text-sm">Ne istersen yaz reis, sana kısa bir video yapayım. (İngilizce açıklama daha iyi sonuç verir.)</p>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                placeholder="Örn: a cute cat walking on a sunny street, cinematic" data-testid="video-prompt-input"
                className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm text-[#e9edef] outline-none border border-transparent focus:border-[#00a884]/50 resize-none" />

              <div className="space-y-2">
                <label className="text-xs text-[#8696a0]">Yön (oran)</label>
                <Segmented testid="video-aspect" value={aspect} onChange={setAspect}
                  options={[{ v: "9:16", label: "Dikey 9:16" }, { v: "16:9", label: "Yatay 16:9" }, { v: "1:1", label: "Kare 1:1" }]} />
              </div>
              <div className="space-y-2">
                <label className="text-xs text-[#8696a0]">Kalite</label>
                <Segmented testid="video-quality" value={quality} onChange={setQuality}
                  options={[{ v: "480p", label: "480p (hızlı)" }, { v: "720p", label: "720p (yüksek)" }]} />
              </div>
              <div className="space-y-2">
                <label className="text-xs text-[#8696a0]">Süre</label>
                <Segmented testid="video-duration" value={duration} onChange={setDuration}
                  options={[{ v: "kisa", label: "Kısa (~5sn)" }, { v: "uzun", label: "Uzun (~8sn)" }]} />
              </div>

              <button onClick={startGenerate} disabled={genBusy || prompt.trim().length < 3} data-testid="video-generate-button"
                className="w-full flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                {genBusy ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
                {genBusy ? "Video hazırlanıyor..." : "Video Üret"}
              </button>
              {genTask && !genErr && <StatusPill status={genTask.status} />}
              {genErr && <p className="flex items-center gap-2 text-sm text-[#ef4444]" data-testid="video-gen-error"><AlertTriangle size={15} /> {genErr}</p>}
              <ResultVideo task={genTask} testid="video-gen" />
            </div>
          )}

          {tab === "i2v" && (
            <div className="space-y-3" data-testid="video-i2v-tab">
              <p className="text-[#8696a0] text-sm">Bir fotoğraf yükle, onu hareketlendireyim. (Max 12MB)</p>
              <input ref={i2vRef} type="file" accept="image/*" className="hidden" data-testid="video-i2v-file-input"
                onChange={(e) => { setI2vFile(e.target.files?.[0] || null); setI2vTask(null); setI2vErr(""); }} />
              <button onClick={() => i2vRef.current?.click()} data-testid="video-i2v-pick-button"
                className="w-full flex items-center justify-center gap-2 bg-[#202c33] hover:bg-[#2a3942] text-[#e9edef] rounded-xl py-3 text-sm transition-colors border border-dashed border-[#2a3942]">
                <Upload size={16} /> {i2vFile ? i2vFile.name : "Fotoğraf seç"}
              </button>
              {i2vFile && (
                <img src={URL.createObjectURL(i2vFile)} alt="önizleme" className="w-full max-h-40 object-contain rounded-xl bg-black" data-testid="video-i2v-preview" />
              )}
              <textarea value={i2vPrompt} onChange={(e) => setI2vPrompt(e.target.value)} rows={2}
                placeholder="Nasıl hareket etsin? Örn: kamera yavaşça yaklaşsın, rüzgar essin" data-testid="video-i2v-prompt-input"
                className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm text-[#e9edef] outline-none border border-transparent focus:border-[#00a884]/50 resize-none" />
              <button onClick={startI2V} disabled={i2vBusy || !i2vFile || i2vPrompt.trim().length < 3} data-testid="video-i2v-button"
                className="w-full flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                {i2vBusy ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={18} />}
                {i2vBusy ? "Video hazırlanıyor..." : "Fotoğrafı Hareketlendir"}
              </button>
              {i2vTask && !i2vErr && <StatusPill status={i2vTask.status} />}
              {i2vErr && <p className="flex items-center gap-2 text-sm text-[#ef4444]" data-testid="video-i2v-error"><AlertTriangle size={15} /> {i2vErr}</p>}
              <ResultVideo task={i2vTask} testid="video-i2v" />
            </div>
          )}

          {tab === "gray" && (
            <div className="space-y-3" data-testid="video-gray-tab">
              <p className="text-[#8696a0] text-sm">Bir video yükle, siyah-beyaz (gri tonlama) haline getireyim. (Max 60MB)</p>
              <input ref={fileRef} type="file" accept="video/*" className="hidden" data-testid="video-gray-file-input"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setGrayTask(null); setGrayErr(""); }} />
              <button onClick={() => fileRef.current?.click()} data-testid="video-gray-pick-button"
                className="w-full flex items-center justify-center gap-2 bg-[#202c33] hover:bg-[#2a3942] text-[#e9edef] rounded-xl py-3 text-sm transition-colors border border-dashed border-[#2a3942]">
                <Upload size={16} /> {file ? file.name : "Video seç"}
              </button>
              <button onClick={startGrayscale} disabled={grayBusy || !file} data-testid="video-grayscale-button"
                className="w-full flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                {grayBusy ? <Loader2 size={18} className="animate-spin" /> : <Film size={18} />}
                {grayBusy ? "Gri yapılıyor..." : "Gri Tonlama Yap"}
              </button>
              {grayTask && !grayErr && <StatusPill status={grayTask.status} />}
              {grayErr && <p className="flex items-center gap-2 text-sm text-[#ef4444]" data-testid="video-gray-error"><AlertTriangle size={15} /> {grayErr}</p>}
              <ResultVideo task={grayTask} testid="video-gray" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoStudio;
